import { isBrowserAdvertisingMeasurementGranted } from '@/lib/consent/advertising-consent'

type DataLayerEntry = Record<string, unknown>

type DataLayerWindow = Window & {
  dataLayer?: DataLayerEntry[]
}

/**
 * GTM is relocated into a Partytown web worker (see `app/layout.tsx`), and
 * `forward: ['dataLayer.push']` means Partytown owns `window.dataLayer.push`
 * until the worker has booted and handed the array back.
 *
 * Anything pushed during that window is DISCARDED — not queued, not replayed.
 * Measured on production 2026-09-02: the handover took ~9.6s, and a probe
 * pushed at t=0 was still absent 28s later. `page_view` fires on window
 * `load`, squarely inside that window, so every `page_view` — and with it
 * every page-context custom dimension (`page_type`, `lp_slug`, `service_city`,
 * `product_slug`) — was being dropped before it reached GTM. The bare
 * `lp_variant` / `lp_content` state pushes were lost the same way.
 *
 * The loss was invisible because it is purely a race: later, user-driven
 * events (a phone click, a video play, opening the booking modal) all happen
 * well after the handover and land normally.
 *
 * So events are held in a module-local queue and only handed to the dataLayer
 * once a push is actually guaranteed to land there.
 */

const POLL_MS = 200

/**
 * Stop waiting eventually. If GTM is blocked outright the handover never
 * comes, and events should still reach `window.dataLayer` rather than being
 * held forever — `tests/booking-conversion.spec.ts` asserts against it.
 */
const GIVE_UP_MS = 30_000

const queue: DataLayerEntry[] = []
let pollId: ReturnType<typeof setInterval> | null = null
let waitedMs = 0

function dataLayerOf(): DataLayerEntry[] {
  const w = window as DataLayerWindow
  if (!w.dataLayer) w.dataLayer = []
  return w.dataLayer
}

function isGtmBootstrap(entry: DataLayerEntry): boolean {
  return typeof entry?.event === 'string' && entry.event === 'gtm.js'
}

/**
 * True when a `push` is guaranteed to reach the array GTM reads.
 *
 * Two ways that holds. Either nobody has patched `push` — no Partytown, which
 * is local dev, Preview deployments and the Playwright suite — or GTM has
 * already written its own `gtm.js` bootstrap entry, which is the first thing
 * it puts in the array and therefore a reliable handover signal.
 */
function canDeliver(dataLayer: DataLayerEntry[]): boolean {
  if (dataLayer.push === Array.prototype.push) return true
  return dataLayer.some(isGtmBootstrap)
}

function drain(): void {
  const dataLayer = dataLayerOf()
  const ready = canDeliver(dataLayer)
  // Last resort: append past whoever owns `push` so nothing is silently lost.
  const forced = !ready && waitedMs >= GIVE_UP_MS
  if (!ready && !forced) return

  while (queue.length > 0) {
    const entry = queue.shift() as DataLayerEntry
    if (ready) dataLayer.push(entry)
    else Array.prototype.push.call(dataLayer, entry)
  }

  if (pollId !== null) {
    clearInterval(pollId)
    pollId = null
  }
}

function watchForHandover(): void {
  if (pollId !== null || queue.length === 0) return
  pollId = setInterval(() => {
    waitedMs += POLL_MS
    drain()
  }, POLL_MS)
}

export function pushEvent(name: string, params: Record<string, unknown> = {}): void {
  // GA4-via-GTM convention: top-level `event` plus flat params so they are
  // available as dataLayer variables in the GTM container.
  pushDataLayerState({ event: name, ...params })
}

/** Queue persistent GTM state without inventing an analytics event. */
export function pushDataLayerState(state: DataLayerEntry): boolean {
  if (!isBrowserAdvertisingMeasurementGranted()) return false
  queue.push(state)
  drain()
  watchForHandover()
  return true
}

/** Test-only reset so module-level queue state does not leak between cases. */
export function __resetPushEventQueueForTests(): void {
  queue.length = 0
  waitedMs = 0
  if (pollId !== null) {
    clearInterval(pollId)
    pollId = null
  }
}
